import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { RejectFeedbackModal } from '../modals/FeedbackModals';
import AcceptInterviewModal from '../modals/AcceptInterviewModal';
import { getInterviews, clearInterviewsCache } from '../../lib/interviewsClient';

const MatchRequestNotification = ({ onClose, onActionComplete, candidateStream, hasBookedFirstCall = false, onBookCall }) => {
  console.log('MatchRequestNotification - candidateStream received:', candidateStream);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const navigate = useNavigate();

  const [showRejectFeedbackModal, setShowRejectFeedbackModal] = useState(false);
  const [decliningRequestInfo, setDecliningRequestInfo] = useState(null);
  
  const [showAcceptInterviewModal, setShowAcceptInterviewModal] = useState(false);
  const [acceptingRequestInfo, setAcceptingRequestInfo] = useState(null);
  const [interviewAccepted, setInterviewAccepted] = useState(false);

  useEffect(() => {
    fetchPendingRequests();
  }, []);

  // Debug: Monitor interviewAccepted state changes
  useEffect(() => {
    console.log('MatchRequestNotification - interviewAccepted state changed to:', interviewAccepted);
  }, [interviewAccepted]);

  const fetchPendingRequests = async () => {
    try {
      const interviews = await getInterviews();

      const matchRequests = (interviews || []).filter(
        (interview) => interview.fields["Stage"] === "Match requested"
      );

      const transformedRequests = matchRequests.map((interview) => ({
        id: interview.id,
        roleTitle: interview.fields["Role Title (from Role)"] || "Unknown Role",
        companyName: interview.fields["Name (from Startup) (from Role)"] || "Unknown Company",
        roleImage: interview.fields["Role Cover Image (from Startup) (from Role)"]?.[0]?.url || 
                  "https://placehold.co/400x300/e2e8f0/64748b?text=No+Image",
        compensation: interview.fields["Compensation (from Role)"] || "Not specified",
        location: interview.fields["Location (from Startup) (from Role)"] || "Location not specified",
        fundingStage: interview.fields["Funding Stage (from Startup) (from Role)"] || "Not specified",
        founderMessage: interview.fields["Match request direct message text"] || "",
        urlId: interview.fields["URL ID (from Role)"] || "",
      }));

      setPendingRequests(transformedRequests);
    } catch (error) {
      console.error("Error fetching pending match requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (requestId, roleTitle) => {
    setActionLoading(prev => ({ ...prev, [requestId]: 'accepting' }));
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Authentication token missing. Please log in again.");
        return;
      }
      
      const updatedData = {
        fields: {
          Stage: "Interviewing"
        },
        interviewId: requestId,
      };
      
      const response = await axios.patch(
        `/api/interviews/updateInterviewRequest`,
        updatedData,
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }
      );
      
      if (response.data && response.data.success) {
        console.log('MatchRequestNotification - Accept successful, setting interviewAccepted to true');
        setPendingRequests(prev => prev.filter(req => req.id !== requestId));
        setInterviewAccepted(true); // Show success step
        clearInterviewsCache();
        // Keep the modal open for the success step
        // Don't call onActionComplete immediately - let it be called when modal closes
        return;
      } else {
        console.error("Accept failed:", response.data?.message || "Update failed with unknown error");
      }
    } catch (error) {
      console.error("Error accepting match request:", error);
      if (error.response) {
        console.error("Server response:", error.response.data);
      } else if (error.request) {
        console.error("No response received:", error.request);
      } else {
        console.error("Request setup error:", error.message);
      }
    } finally {
      setActionLoading(prev => ({ ...prev, [requestId]: null }));
    }
  };

    const handleAcceptClick = (request) => {
    setAcceptingRequestInfo(request);
    setShowAcceptInterviewModal(true);
  };

  const handleConfirmAccept = async () => {
    console.log('MatchRequestNotification - handleConfirmAccept called');
    if (acceptingRequestInfo) {
      console.log('MatchRequestNotification - Calling handleAccept for:', acceptingRequestInfo.roleTitle);
      await handleAccept(acceptingRequestInfo.id, acceptingRequestInfo.roleTitle);
      console.log('MatchRequestNotification - handleAccept completed');
    }
    // Keep modal open to show success step
  };
 
   const handleDecline = (requestId, roleTitle) => {
     setDecliningRequestInfo({ id: requestId, title: roleTitle });
     setShowRejectFeedbackModal(true);
   };

  const handleDeclineSubmitWithFeedback = async (feedbackData) => {
    if (!decliningRequestInfo) return;

    const { id: requestId, title: roleTitle } = decliningRequestInfo;
    setActionLoading(prev => ({ ...prev, [requestId]: 'declining' }));
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Authentication token missing. Please log in again.");
        setShowRejectFeedbackModal(false);
        setDecliningRequestInfo(null);
        return;
      }
      
      const fields = {
        Stage: "Rejected Interview Request",
        "MR Rejection Feedback": feedbackData.reason,
        "Candidate Interview Stage": "Rejected Match Request",
      };

      if (feedbackData.reason === "Other" && feedbackData.otherText) {
        fields["MR Rejection Feedback (other)"] = feedbackData.otherText;
      }

      const updatedData = {
        fields: fields,
        interviewId: requestId,
      };
      
      console.log("Sending decline request with feedback and data:", updatedData);
      
      const response = await axios.patch(
        `/api/interviews/updateInterviewRequest`,
        updatedData,
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000 
        }
      );
      
      console.log("Response from decline with feedback:", response.data);
      
      if (response.data && response.data.success) {
        setPendingRequests(prev => prev.filter(req => req.id !== requestId));
        if (onActionComplete) {
          onActionComplete('declined', roleTitle);
        }
        clearInterviewsCache();
        if (pendingRequests.length === 1) {
            if (onClose) onClose();
        }
      } else {
        console.error("Decline with feedback failed:", response.data?.message || "Update failed with unknown error");
      }
    } catch (error) {
      console.error("Error declining match request with feedback:", error);
      if (error.response) {
        console.error("Server response:", error.response.data);
      } else if (error.request) {
        console.error("No response received:", error.request);
      } else {
        console.error("Request setup error:", error.message);
      }
    } finally {
      setActionLoading(prev => ({ ...prev, [requestId]: null }));
      setShowRejectFeedbackModal(false);
      setDecliningRequestInfo(null);
    }
  };

  const handleViewDetails = () => {
    navigate('/interviews');
    if (onClose) onClose();
  };

  const handleLearnMore = (request) => {
    if (request.urlId) {
      sessionStorage.setItem("lastVisitedPage", window.location.pathname);
      window.open(`/role?roleId=${request.urlId}&from=interview-request&interviewId=${request.id}`, "_blank");
    }
  };

  const handleCardClick = (request) => {
    handleLearnMore(request);
  };

  if (!loading && pendingRequests.length === 0 && !showRejectFeedbackModal) {
    if (onClose) {
        // Add a micro-delay to allow any state updates from action to complete before closing.
        // This can prevent React warning about setting state on unmounted component if onActionComplete causes parent re-render.
        // setTimeout(onClose, 0); 
        // Decided against setTimeout as it might complicate flow if onClose itself triggers state changes.
        // The current logic of closing after a successful action (accept/decline) if pendingRequests.length becomes 1 (meaning it will be 0 after filter)
        // should handle this. If this is being called when it's already 0, it means the modal was opened with 0 requests.
    }
    return null; 
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
        onClick={(e) => {
          if (e.target === e.currentTarget && !showRejectFeedbackModal) {
            if (onClose) onClose();
          }
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-6 py-5 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#02B491]/10 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-[#02B491]" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Interview Requests</h3>
                  <p className="text-gray-600 text-sm">
                    {loading ? 'Loading...' : `${pendingRequests.length} ${pendingRequests.length === 1 ? 'company wants' : 'companies want'} to interview you`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => { if (onClose && !showRejectFeedbackModal) onClose(); }}
                className="text-gray-400 hover:text-gray-600 p-2 transition-colors rounded-lg hover:bg-gray-100"
                disabled={showRejectFeedbackModal}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6 flex-grow overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#02B491]"></div>
              </div>
            ) : (
              <>
                <div className="mb-5">
                  <p className="text-gray-600 text-sm">
                    Respond quickly to show your enthusiasm and connect over email to arrange a first interview.
                  </p>
                </div>

                <div className="space-y-4">
                  {pendingRequests.map((request) => (
                    <motion.div
                      key={request.id}
                      layout
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20, transition: { duration: 0.2 } }}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => handleCardClick(request)}
                    >
                      <div className="flex items-start gap-4">
                        <img
                          src={request.roleImage}
                          alt={request.companyName}
                          className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 truncate">
                            {request.roleTitle}
                          </h4>
                          <p className="text-sm text-gray-600">{request.companyName}</p>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-gray-500">
                            <span>{request.location}</span>
                            <span>{request.compensation}</span>
                            {request.fundingStage && <span>{request.fundingStage}</span>}
                          </div>
                          
                          {request.founderMessage && (
                            <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                              <div className="flex items-start gap-2">
                                <svg className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.691 1.359 3.055 3.037 3.055h9.426c1.678 0 3.037-1.364 3.037-3.055v-9.42c0-1.691-1.359-3.055-3.037-3.055H3.787c-1.678 0-3.037 1.364-3.037 3.055v9.42Z" />
                                </svg>
                                <div className="flex-1">
                                  <p className="text-xs font-medium text-gray-600 mb-1">Message from founder</p>
                                  <p className="text-sm text-gray-800 leading-relaxed">{request.founderMessage}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 flex justify-end space-x-3" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => handleLearnMore(request)}
                          className="px-4 py-2 text-sm font-medium text-[#02B491] bg-white border border-[#02B491] hover:bg-[#02B491] hover:text-white rounded-lg transition-colors"
                        >
                          Learn More
                        </button>
                        <button
                          onClick={() => handleDecline(request.id, request.roleTitle)}
                          disabled={actionLoading[request.id]}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {actionLoading[request.id] === 'declining' ? 'Declining...' : 'Decline'}
                        </button>
                        <button
                          onClick={() => handleAcceptClick(request)}
                          disabled={actionLoading[request.id]}
                          className="px-4 py-2 text-sm font-medium text-white bg-[#02B491] hover:bg-[#018268] rounded-lg transition-colors disabled:opacity-50"
                        >
                          {actionLoading[request.id] === 'accepting' ? 'Accepting...' : 'Accept'}
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </div>

          {!loading && pendingRequests.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={handleViewDetails}
                className="w-full text-center px-4 py-2 text-sm font-medium text-[#02B491] hover:text-[#018268] hover:bg-[#02B491]/10 rounded-lg transition-colors"
              >
                View All Interview Requests
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>

      {showRejectFeedbackModal && decliningRequestInfo && (
        <RejectFeedbackModal
          isOpen={showRejectFeedbackModal}
          onClose={() => {
            setShowRejectFeedbackModal(false);
            setDecliningRequestInfo(null);
          }}
          onSubmit={handleDeclineSubmitWithFeedback}
          roleTitle={decliningRequestInfo.title}
          onAccept={() => handleAccept(decliningRequestInfo.id, decliningRequestInfo.title)}
        />
      )}

      {showAcceptInterviewModal && acceptingRequestInfo && (
        <AcceptInterviewModal
            isOpen={showAcceptInterviewModal}
            onClose={() => {
              console.log('MatchRequestNotification - AcceptInterviewModal onClose, interviewAccepted:', interviewAccepted);
              // Call onActionComplete when modal is closed after successful accept
              if (interviewAccepted && onActionComplete && acceptingRequestInfo) {
                onActionComplete('accepted', acceptingRequestInfo.roleTitle);
              }
              setShowAcceptInterviewModal(false);
              setAcceptingRequestInfo(null);
              setInterviewAccepted(false); // Reset for next time
              if (pendingRequests.length === 0) {
                onClose(); // Close the main notification if no more pending requests
              }
            }}
            onConfirm={handleConfirmAccept}
            roleTitle={acceptingRequestInfo.roleTitle}
            companyName={acceptingRequestInfo.companyName}
            candidateStream={candidateStream}
            interviewAccepted={interviewAccepted}
            hasBookedFirstCall={hasBookedFirstCall}
            onBookCall={onBookCall}
          />
      )}
    </AnimatePresence>
  );
};

export default MatchRequestNotification; 